/**
 * Global Notification Banner Component
 * A unified, slim banner for all app-wide notifications
 * Replaces overlapping pop-ups with a clean, consistent design
 */
import React, { useState, useEffect, useCallback } from 'react';
import './GlobalNotificationBanner.css';

// Notification types with corresponding icons and colors
const NOTIFICATION_TYPES = {
  info: { icon: 'ℹ️', bgClass: 'bg-blue-50', borderClass: 'border-blue-200', textClass: 'text-blue-900' },
  success: { icon: '✓', bgClass: 'bg-green-50', borderClass: 'border-green-200', textClass: 'text-green-900' },
  warning: { icon: '⚠️', bgClass: 'bg-yellow-50', borderClass: 'border-yellow-200', textClass: 'text-yellow-900' },
  error: { icon: '✕', bgClass: 'bg-red-50', borderClass: 'border-red-200', textClass: 'text-red-900' }
};

// Global notification manager (singleton pattern)
class NotificationManager {
  constructor() {
    this.listeners = [];
    this.currentNotification = null;
    this.throttleTimers = new Map(); // Track throttling for duplicate messages
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify(listeners) {
    this.listeners.forEach(listener => listener(this.currentNotification));
  }

  show(notification) {
    const key = `${notification.type}-${notification.message}`;
    
    // Check if this exact notification was recently shown (throttling)
    if (this.throttleTimers.has(key)) {
      console.log('Throttling duplicate notification:', key);
      return;
    }

    // If same notification is already showing, don't duplicate
    if (this.currentNotification && 
        this.currentNotification.message === notification.message &&
        this.currentNotification.type === notification.type) {
      console.log('Notification already showing, skipping duplicate');
      return;
    }

    this.currentNotification = { ...notification, id: Date.now() };
    this.notify();

    // Set throttle timer (prevent same notification for X minutes)
    if (notification.throttleMinutes) {
      this.throttleTimers.set(key, true);
      setTimeout(() => {
        this.throttleTimers.delete(key);
      }, notification.throttleMinutes * 60 * 1000);
    }

    // Auto-dismiss for non-critical notifications
    if (notification.autoDismiss) {
      setTimeout(() => {
        this.dismiss();
      }, notification.autoDismissDuration || 7000);
    }
  }

  dismiss() {
    this.currentNotification = null;
    this.notify();
  }

  update(updates) {
    if (this.currentNotification) {
      this.currentNotification = { ...this.currentNotification, ...updates };
      this.notify();
    }
  }
}

// Export singleton instance
export const notificationManager = new NotificationManager();

// Main component
const GlobalNotificationBanner = () => {
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    // Subscribe to notification manager
    const unsubscribe = notificationManager.subscribe((notif) => {
      setNotification(notif);
    });

    return unsubscribe;
  }, []);

  const handleDismiss = useCallback(() => {
    if (notification?.onDismiss) {
      notification.onDismiss();
    }
    notificationManager.dismiss();
  }, [notification]);

  const handlePrimaryAction = useCallback(() => {
    if (notification?.primaryAction?.onClick) {
      notification.primaryAction.onClick();
    }
    // Don't auto-dismiss after action - let the action handler control that
  }, [notification]);

  const handleSecondaryAction = useCallback(() => {
    if (notification?.secondaryAction?.onClick) {
      notification.secondaryAction.onClick();
    }
    notificationManager.dismiss();
  }, [notification]);

  if (!notification) return null;

  const typeConfig = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.info;

  return (
    <div className={`global-notification-banner ${typeConfig.bgClass} ${typeConfig.borderClass}`}>
      <div className="notification-content">
        {/* Icon and Message */}
        <div className="notification-main">
          <span className="notification-icon">{typeConfig.icon}</span>
          <div className="notification-text">
            <strong className={typeConfig.textClass}>{notification.title || notification.message}</strong>
            {notification.description && (
              <p className={`notification-description ${typeConfig.textClass}`}>
                {notification.description}
              </p>
            )}
            {notification.countdown && (
              <p className={`notification-countdown ${typeConfig.textClass}`}>
                {notification.countdown}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="notification-actions">
          {notification.primaryAction && (
            <button
              onClick={handlePrimaryAction}
              className="notification-btn notification-btn-primary"
              disabled={notification.primaryAction.disabled}
            >
              {notification.primaryAction.label}
            </button>
          )}
          
          {notification.secondaryAction && (
            <button
              onClick={handleSecondaryAction}
              className="notification-btn notification-btn-secondary"
            >
              {notification.secondaryAction.label}
            </button>
          )}

          {notification.dismissible !== false && (
            <button
              onClick={handleDismiss}
              className="notification-close"
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalNotificationBanner;
